import type { ReactNode } from "react";
import { Accordion, ActionIcon, Badge, Box, Group, NumberInput, Select, TagsInput, Text, TextInput, Tooltip } from "@mantine/core";
import { IconArrowBackUp } from "@tabler/icons-react";
import { useTranslation } from "@hooks/useTranslation";
import { getPath, isUnset, setPath } from "./optionPath";
import type { OptionSectionSpec, OptionSpec } from "./specs";
import { spacePx } from "@xplatform/design-system";
import "./OptionSections.scss";

type Bag = object;

interface OptionFieldProps {
    spec: OptionSpec;
    value: unknown;
    onChange: (next: unknown) => void;
}

const OptionField = ({ spec, value, onChange }: OptionFieldProps) => {
    const { t } = useTranslation();
    const label = t(`admin.flowConfigs.options.${spec.id}.label`);
    const hint = t(`admin.flowConfigs.options.${spec.id}.hint`);
    const overridden = !isUnset(value);
    const inheritLabel = t("admin.flowConfigs.options.inheritRuntime");
    const fieldLabel = (
        <span className="option-field__label">
            {label}
            {overridden && <span className="option-field__dot" aria-hidden />}
        </span>
    );
    // The label carries a marker, so the accessible name is set explicitly.
    const common = { label: fieldLabel, "aria-label": label, description: hint };

    let input: ReactNode;
    switch (spec.kind) {
        case "boolean":
            input = (
                <Select
                    {...common}
                    value={isUnset(value) ? "inherit" : String(value)}
                    data={[
                        { value: "inherit", label: inheritLabel },
                        { value: "true", label: t("admin.flowConfigs.options.enabled") },
                        { value: "false", label: t("admin.flowConfigs.options.disabled") },
                    ]}
                    allowDeselect={false}
                    onChange={(selected) => onChange(selected === "inherit" || selected === null ? undefined : selected === "true")}
                    comboboxProps={{ withinPortal: false }}
                />
            );
            break;
        case "choice":
            input = (
                <Select
                    {...common}
                    value={isUnset(value) ? "inherit" : String(value)}
                    data={[
                        { value: "inherit", label: inheritLabel },
                        ...spec.choices.map(([choice, choiceLabel]) => ({
                            value: choice,
                            label: t(`admin.flowConfigs.options.choices.${choiceLabel}`),
                        })),
                    ]}
                    allowDeselect={false}
                    onChange={(selected) => onChange(selected === "inherit" ? undefined : selected)}
                    comboboxProps={{ withinPortal: false }}
                />
            );
            break;
        case "number":
            input = (
                <NumberInput
                    {...common}
                    value={(value as number | undefined) ?? ""}
                    min={spec.min}
                    max={spec.max}
                    step={spec.step}
                    decimalScale={spec.decimalScale}
                    placeholder={inheritLabel}
                    onChange={(next) => onChange(next === "" ? undefined : Number(next))}
                />
            );
            break;
        case "text":
            input = (
                <TextInput
                    {...common}
                    value={(value as string | undefined) ?? ""}
                    placeholder={inheritLabel}
                    onChange={(event) => onChange(event.currentTarget.value)}
                />
            );
            break;
        case "tags":
            input = (
                <TagsInput
                    {...common}
                    value={(value as string[] | undefined) ?? []}
                    splitChars={[","]}
                    placeholder={isUnset(value) ? inheritLabel : undefined}
                    onChange={(next) => onChange(next.length ? next : undefined)}
                    comboboxProps={{ withinPortal: false }}
                />
            );
            break;
    }

    return (
        <Group
            gap={spacePx[1]}
            align="flex-end"
            wrap="nowrap"
            className="option-field"
        >
            <Box className="option-field__input">{input}</Box>
            <Tooltip label={t("admin.flowConfigs.options.reset")} openDelay={300}>
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label={t("admin.flowConfigs.options.resetField", { label })}
                    onClick={() => onChange(undefined)}
                    className="option-field__reset"
                    style={{ visibility: overridden ? "visible" : "hidden" }}
                    tabIndex={overridden ? 0 : -1}
                    mb={spacePx[1]}
                >
                    <IconArrowBackUp size={16} />
                </ActionIcon>
            </Tooltip>
        </Group>
    );
};

/** How many options of `section` are set on `value` (i.e. override the default). */
export const countOverrides = (section: OptionSectionSpec, value: unknown) =>
    section.fields.filter((spec) => !isUnset(getPath(value, spec.path))).length;

interface OptionSectionsProps<T extends Bag> {
    sections: OptionSectionSpec[];
    value: T | null;
    onChange: (next: T | null) => void;
    /** Extra controls rendered at the end of a section (not described by a spec). */
    renderExtra?: (sectionId: string) => ReactNode;
    /** Overrides held by those extra controls, added to the section's count. */
    extraOverrides?: (sectionId: string) => number;
}

/**
 * Runtime options as titled groups. Every field explains itself, marks when it
 * overrides the runtime default, and can be reset to the default on its own.
 * Basic groups are always shown; advanced ones are collapsed, and their header
 * says how many overrides they hold so nothing set is ever hidden silently.
 */
export function OptionSections<T extends Bag>({ sections, value, onChange, renderExtra, extraOverrides }: OptionSectionsProps<T>) {
    const { t } = useTranslation();
    const basic = sections.filter((section) => !section.advanced);
    const advanced = sections.filter((section) => section.advanced);

    const overridesOf = (section: OptionSectionSpec) =>
        countOverrides(section, value) + (extraOverrides?.(section.id) ?? 0);

    const resetSection = (section: OptionSectionSpec) => {
        let next: T | null = value;
        for (const spec of section.fields) next = setPath(next, spec.path, undefined);
        onChange(next);
    };

    const fields = (section: OptionSectionSpec) => (
        <>
            <Box className="config-options-grid">
                {section.fields.map((spec) => (
                    <OptionField
                        key={spec.id}
                        spec={spec}
                        value={getPath(value, spec.path)}
                        onChange={(next) => onChange(setPath(value, spec.path, next))}
                    />
                ))}
            </Box>
            {renderExtra?.(section.id)}
        </>
    );

    const overrideBadge = (count: number) =>
        count > 0 ? (
            <Badge size="sm" variant="light" color="orange">
                {t("admin.flowConfigs.options.overrides", { count })}
            </Badge>
        ) : null;

    return (
        <Box className="option-sections">
            {basic.map((section) => (
                <Box key={section.id} className="option-sections__basic">
                    <Group justify="space-between" mb="xs">
                        <Group gap="xs">
                            <Text size="sm" fw={600}>{t(`admin.flowConfigs.optionSections.${section.id}`)}</Text>
                            {overrideBadge(overridesOf(section))}
                        </Group>
                        {countOverrides(section, value) > 0 && (
                            <Text
                                component="button"
                                type="button"
                                size="xs"
                                c="dimmed"
                                className="option-sections__reset"
                                onClick={() => resetSection(section)}
                            >
                                {t("admin.flowConfigs.options.resetSection")}
                            </Text>
                        )}
                    </Group>
                    {fields(section)}
                </Box>
            ))}

            {advanced.length > 0 && (
                <Box>
                    <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={spacePx[1]}>
                        {t("admin.flowConfigs.options.advanced")}
                    </Text>
                    <Accordion multiple variant="separated" chevronPosition="left" className="option-sections__advanced">
                        {advanced.map((section) => (
                            <Accordion.Item key={section.id} value={section.id}>
                                <Accordion.Control>
                                    <Group gap="xs">
                                        <Text size="sm" fw={600}>{t(`admin.flowConfigs.optionSections.${section.id}`)}</Text>
                                        {overrideBadge(overridesOf(section))}
                                    </Group>
                                </Accordion.Control>
                                <Accordion.Panel>
                                    {countOverrides(section, value) > 0 && (
                                        <Group justify="flex-end" mb="xs">
                                            <Text
                                                component="button"
                                                type="button"
                                                size="xs"
                                                c="dimmed"
                                                className="option-sections__reset"
                                                onClick={() => resetSection(section)}
                                            >
                                                {t("admin.flowConfigs.options.resetSection")}
                                            </Text>
                                        </Group>
                                    )}
                                    {fields(section)}
                                </Accordion.Panel>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </Box>
            )}
        </Box>
    );
}
